import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { useSiteContent } from "@/context/SiteContentContext";
import { sectionScheme } from "@/lib/schemes";
import Seo from "@/components/Seo";

/**
 * Shop — curated archive, not a Shopify storefront.
 *   • Everything centered
 *   • Header sections auto-hide when their copy is empty
 *   • No product-category tiles (curated order only, small catalog)
 *   • Product cards → link directly to /shop/:id (their own page)
 *   • Original photography preserved (no experimental rendering here)
 *   • All outbound buys go to Etsy — "shopify" language removed
 */

const Pill = ({ active, onClick, testid, children }) => (
    <button
        onClick={onClick}
        data-testid={testid}
        className={`font-mono text-[11px] capitalize tracking-[0.05em] px-3.5 py-1.5 rounded-full border transition-colors ${
            active
                ? "bg-current text-transparent border-current"
                : "border-current/30 opacity-70 hover:opacity-100"
        }`}
        style={active ? { color: "var(--pill-active-bg, currentColor)" } : undefined}
    >
        <span style={active ? { color: "var(--pill-active-fg, #FFFFFF)" } : undefined}>{children}</span>
    </button>
);

export default function Shop() {
    const { t, lang, currency: globalCurrency, setCurrency: setGlobalCurrency } = useT();
    const { content } = useSiteContent();
    const [products, setProducts] = useState([]);
    const [sort, setSort] = useState("added");
    const [artistFilter, setArtistFilter] = useState("all");
    const [typeFilter, setTypeFilter] = useState("all");
    // Global currency — lives in the language context so EN/USD and ES/MXN
    // stay paired site-wide. The local toggle below still allows overrides.
    const displayCurrency = globalCurrency;
    const setDisplayCurrency = setGlobalCurrency;
    const fxUsdToMxn = parseFloat(content.fx_usd_to_mxn || "20") || 20;

    useEffect(() => {
        api.get("/products").then((r) => setProducts(r.data));
    }, []);

    const artists = useMemo(() => {
        const set = new Set(products.map((p) => p.artist_credit).filter(Boolean));
        return ["all", ...Array.from(set)];
    }, [products]);
    const types = useMemo(() => {
        const set = new Set(products.map((p) => p.product_type).filter(Boolean));
        return ["all", ...Array.from(set)];
    }, [products]);

    const filtered = useMemo(() => {
        let list = products.slice();
        if (artistFilter !== "all") list = list.filter((p) => p.artist_credit === artistFilter);
        if (typeFilter !== "all") list = list.filter((p) => p.product_type === typeFilter);
        switch (sort) {
            case "price_asc":
                list.sort((a, b) => (a.price || 0) - (b.price || 0));
                break;
            case "price_desc":
                list.sort((a, b) => (b.price || 0) - (a.price || 0));
                break;
            case "added":
            default:
                list.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
        }
        return list;
    }, [products, sort, artistFilter, typeFilter]);

    // Header copy — hide the whole block if all fields are empty.
    const kicker = content[`shop_page_kicker_${lang}`] || content.shop_page_kicker_en || "";
    const title = content[`shop_page_title_${lang}`] || content.shop_page_title_en || "";
    const intro = content[`shop_page_intro_${lang}`] || content.shop_page_intro_en || "";
    const hasHeader = !!(kicker || title || intro);

    // Banner width % — admin controls how much of the row the promo banner occupies
    const bannerWidthPct = Math.max(30, Math.min(100, parseInt(content.shop_banner_width_pct || "60", 10) || 60));

    return (
        <div data-testid="shop-page" className="px-4 md:px-8 pt-14 md:pt-20 pb-8 md:pb-14">
            <Seo
                title={`${title || "Shop"} | ITZKATU`}
                description="tienda del colectivo itzkatu — piezas únicas, apparel y ediciones limitadas desde san cristóbal de las casas."
                path="/shop"
            />
          <div className="max-w-6xl mx-auto">
            {/* HEADER — auto-hides when empty. Left-aligned, same margin as everything else. */}
            {hasHeader && (
                <section className="pb-10 md:pb-14 max-w-2xl">
                    {kicker && (
                        <div className="font-mono text-[10px] uppercase tracking-[0.4em] opacity-60 mb-4">
                            {kicker}
                        </div>
                    )}
                    {title && (
                        <h1 className="font-heading font-black text-5xl md:text-7xl uppercase tracking-tighter leading-[0.9]">
                            {title.replace(/\.$/, "")}
                        </h1>
                    )}
                    {intro && (
                        <p className="mt-5 font-body text-sm md:text-base leading-relaxed opacity-80 max-w-lg">
                            {intro}
                        </p>
                    )}
                    {/* Global currency toggle — below title, applies to every product tile */}
                    <div className="mt-6 inline-flex border border-current rounded-sm overflow-hidden" data-testid="shop-currency-toggle">
                        {["USD", "MXN"].map((c, i) => (
                            <button
                                key={c}
                                onClick={() => setDisplayCurrency(c)}
                                data-testid={`shop-currency-${c.toLowerCase()}`}
                                className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 transition-colors ${displayCurrency === c ? "bg-current" : "opacity-70 hover:opacity-100"} ${i === 0 ? "border-r border-current" : ""}`}
                            >
                                <span className={displayCurrency === c ? "text-[color:var(--sc-bg)]" : ""}>{c}</span>
                            </button>
                        ))}
                    </div>
                </section>
            )}

            {/* PROMO BANNER — single, admin-editable, width configurable */}
            {(content.shop_page_banner_1_image_url || content[`shop_page_banner_1_label_${lang}`] || content.shop_page_banner_1_label_en) && (
                <section className="pb-10 md:pb-12">
                    <div style={{ width: `${bannerWidthPct}%` }} data-testid="shop-promo-banner">
                        <ShopBannerBlock content={content} lang={lang} />
                    </div>
                </section>
            )}

            {/* FILTERS — sort row on top (label left · plain-text options right).
                Filter chips (type/artist) sit below as pills. Everything centered vertically.
                Labels return per user feedback ("type ·" / "artist ·" / "sort ·"). */}
            {(() => {
                const showTypeFilter = types.length > 1 && content.shop_show_type_filter !== "0";
                const showArtistFilter = artists.length > 1 && content.shop_show_artist_filter !== "0";
                const hasBelow = showTypeFilter || showArtistFilter;
                return (
                <section data-testid="shop-filters" className="pb-10 md:pb-14">
                    <div className="flex flex-col gap-4">
                        {/* Sort row — label LEFT, options RIGHT as plain text (not pills).
                            The divider only appears when there ARE filters below (type/artist). */}
                        <div className={`flex flex-wrap items-baseline justify-between gap-3 ${hasBelow ? "pb-3 border-b border-current/10" : ""}`}>
                            <span className="font-mono text-[11px] capitalize tracking-[0.05em] opacity-70">
                                {t("shop_sort_by") || "sort"}
                            </span>
                            <div className="flex items-center gap-4">
                                {["added", "price_asc", "price_desc"].map((k) => (
                                    <button
                                        key={k}
                                        onClick={() => setSort(k)}
                                        data-testid={`shop-sort-${k}`}
                                        className={`font-mono text-[11px] capitalize tracking-[0.05em] transition-colors ${
                                            sort === k ? "text-[color:var(--sc-accent)]" : "opacity-60 hover:opacity-100"
                                        }`}
                                    >
                                        {t(`shop_sort_${k}`)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Type filter row — label LEFT, pills RIGHT (same style as artist row) */}
                        {showTypeFilter && (
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <span className="font-mono text-[11px] capitalize tracking-[0.05em] opacity-70">
                                    {t("shop_filter_type") || "type"}
                                </span>
                                <div className="flex flex-wrap items-center gap-2">
                                    {types.map((k) => (
                                        <Pill key={k} active={typeFilter === k} onClick={() => setTypeFilter(k)} testid={`shop-type-${k}`}>
                                            {k === "all" ? t("shop_filter_all") : k}
                                        </Pill>
                                    ))}
                                </div>
                            </div>
                        )}

                        {showArtistFilter && (
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <span className="font-mono text-[11px] capitalize tracking-[0.05em] opacity-70">
                                    {t("shop_filter_artist") || "artist"}
                                </span>
                                <div className="flex flex-wrap items-center gap-2">
                                    {artists.map((k) => (
                                        <Pill key={k} active={artistFilter === k} onClick={() => setArtistFilter(k)} testid={`shop-artist-${k}`}>
                                            {k === "all" ? t("shop_filter_all") : k}
                                        </Pill>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </section>
                );
            })()}

            {/* GRID — 4 per row, links to /shop/:id */}
            <section>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-14 md:gap-x-10 md:gap-y-20">
                    {filtered.map((p) => (
                        <ShopCard key={p.id} product={p} displayCurrency={displayCurrency} fxUsdToMxn={fxUsdToMxn} />
                    ))}
                </div>
                {filtered.length === 0 && (
                    <div className="py-24 text-center font-mono text-xs uppercase tracking-[0.3em] opacity-50">
                        {t("shop_empty")}
                    </div>
                )}
            </section>
          </div>
        </div>
    );
}

function ShopCard({ product: p, displayCurrency = "USD", fxUsdToMxn = 20 }) {
    const { content } = useSiteContent();
    const finalPrice = p.sale_percent > 0 ? Number(p.price) * (1 - p.sale_percent / 100) : Number(p.price);
    // Convert to display currency from the product's own base currency.
    const baseCurrency = p.currency === "MXN" ? "MXN" : (p.currency === "EUR" ? "EUR" : "USD");
    function convert(v) {
        if (baseCurrency === displayCurrency) return v;
        if (baseCurrency === "USD" && displayCurrency === "MXN") return v * fxUsdToMxn;
        if (baseCurrency === "MXN" && displayCurrency === "USD") return v / fxUsdToMxn;
        return v;
    }
    const cur = displayCurrency === "MXN" ? "$" : (displayCurrency === "EUR" ? "€" : "$");
    const suffix = displayCurrency === "MXN" ? " MXN" : (displayCurrency === "USD" ? " USD" : "");
    // Strip trailing zeros — "$800.00" → "$800", "$499.95" stays.
    const fmt = (v) => {
        const n = Number(v);
        if (!isFinite(n)) return "0";
        return n.toFixed(2).replace(/\.?0+$/, "");
    };
    // Product title typography — unified with scene tile titles (MediaTile caps).
    return (
        <Link
            to={`/shop/${p.id}`}
            data-testid={`shop-item-${p.id}`}
            className="group flex flex-col items-start text-left"
        >
            {/* Tall rectangular tile with WHITE background — product photos
                are `object-contain` and centered vertically so they never
                get cropped or up-scaled. */}
            <div className="w-full aspect-[3/4] overflow-hidden relative flex items-center justify-center">
                {p.image_url && (
                    <img
                        src={p.image_url}
                        alt={p.title}
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        className="max-w-full max-h-full w-auto h-auto object-contain transition-opacity duration-500"
                    />
                )}
                {p.image_url_alt && (
                    <img
                        src={p.image_url_alt}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        className="absolute inset-0 m-auto max-w-full max-h-full w-auto h-auto object-contain opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    />
                )}
                {p.sold_out && (
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-black text-white font-mono text-[10px] uppercase tracking-[0.25em] px-2.5 py-1">sold out</div>
                )}
                {!p.sold_out && p.sale_percent > 0 && (
                    <div className="absolute top-3 right-3 bg-[#5D3FD3] text-white font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-1">-{p.sale_percent}%</div>
                )}
            </div>
            <h3 className="mt-2 font-heading font-black text-lg md:text-xl uppercase tracking-tight leading-[1.1] group-hover:text-[color:var(--sc-accent)] transition-colors">
                {p.title}
            </h3>
            {p.product_type && (
                <div className="mt-1 font-body italic text-xs md:text-sm opacity-60">
                    {p.product_type}
                </div>
            )}
            <div className="mt-2 font-mono text-sm">
                {p.sale_percent > 0 && !p.sold_out ? (
                    <>
                        <span className="text-[color:var(--sc-accent)]">{cur}{fmt(convert(finalPrice))}{suffix}</span>{" "}
                        <span className="opacity-40 line-through text-xs">{cur}{fmt(convert(Number(p.price)))}</span>
                    </>
                ) : (
                    <span className={p.sold_out ? "opacity-40 line-through" : ""}>
                        {cur}{fmt(convert(Number(p.price)))}{suffix}
                    </span>
                )}
            </div>
        </Link>
    );
}

function ShopBannerBlock({ content, lang }) {
    const img = content.shop_page_banner_1_image_url;
    const label = content[`shop_page_banner_1_label_${lang}`] || content.shop_page_banner_1_label_en;
    const link = content.shop_page_banner_1_link;
    const bannerScheme = content.shop_page_banner_scheme;
    let bg = "bg-[#F5F5F5]", fg = "";
    if (bannerScheme) {
        const s = sectionScheme(bannerScheme);
        bg = s.bg;
        fg = s.fg;
    }
    const inner = (
        <div className={`relative aspect-[20/7] overflow-hidden group ${bg} ${fg}`}>
            {img && <img src={img} alt={label || "banner"} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />}
            {label && (
                <div className="absolute inset-0 flex items-center justify-center p-3">
                    <div className={`font-heading font-black text-lg md:text-2xl uppercase tracking-tight text-center ${img && !bannerScheme ? "text-white" : ""}`}>
                        {label}
                    </div>
                </div>
            )}
        </div>
    );
    if (link) {
        return link.startsWith("/")
            ? <Link to={link}>{inner}</Link>
            : <a href={link} target="_blank" rel="noreferrer">{inner}</a>;
    }
    return inner;
}
